import Heading from "Components/Heading";
import React, { useMemo, useState } from "react";
import { Button, Card, Col, Row } from "react-bootstrap";
import { BiExport, BiLike } from "react-icons/bi";
import Footer from "Components/Footer";
import Loader from "Components/Loader";
import SearchForm from "Components/SearchForm";
import SampleDataTable from "Components/DataTables";
import { formatDate } from "Helper/Converters";
import { toast } from "react-toastify";
import { exportFileToExcel } from "Utils/utils";
import { useSelector } from "react-redux";

const SearchTicket = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [searchedData, setSearchedData] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);

  const handleSelectionChange = (selectedIds) => {
    setSelectedIds(selectedIds);
  };

  const permissions = useSelector((state) => state.auth?.department?.permissions) || [];

  const canViewAssignedDetails = permissions.includes(
    "view_ticket_assigned_details"
  );

  const columns = useMemo(() => {

    const baseColumns = [
      {
        accessor: "ticketNo",
        Header: "Ticket No",
        Cell: ({ row }) => {
        const showFlag = Boolean(row.original?.hasHaltonDualStationFlag);
  return (
    <a
      onClick={(e) => {
        e.preventDefault();
        const url = `/dashboard/ticket-detail/${row.original.ticketNo}`;
        const windowName = `popUpWindow_${row.original.ticketNo}`;
        const windowSize = "width=800,height=600";
        window.open(url, windowName, windowSize);
      }}
      href={`/dashboard/ticket-detail/${row.original.ticketNo}`}
      rel="noopener noreferrer"
      className={`badge ${row.original?.cancelDate && "custom-badge"} ${
        row.original.ticketType == "EMERGENCY" ||
        row.original.priority == "EMERGENCY"
          ? "emergency"
          : row.original.ticketType == "MULTIPLE" ||
            row.original.priority == "MULTIPLE"
          ? "multiple"
          : row.original.ticketType == "PRIORITY" ||
            row.original.priority == "PRIORITY"
          ? "priority"
          : row.original.ticketType == "PROJECT WORK" ||
            row.original.priority == "PROJECT WORK"
          ? "projectWork"
          : row.original.ticketType == "STANDARD" ||
            row.original.priority == "STANDARD"
          ? "standard"
          : "default"
      }`}
    >
      <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.1 }}>
        <span>{row.original.ticketNo}</span>
        {showFlag && (
          <span
            title="Raw ticket contains both HLTNW02 + HLTNW01 assigned to TCU FOR HALTON REGIO"
            style={{
              marginTop: "4px",
              fontSize: "28px",
              display: "inline-block",
            }}
          >
            🚩
          </span>
        )}
        {row.original?.cancelDate && <span className="cancel-btn"></span>}
      </div>
    </a>
  );
},
      },
      {
        accessor: "location",
        Header: "Location",
        Cell: ({ row }) => {
          return (
            <div style={{ whiteSpace: "pre-line" }}>
              {row.original.digAddress} {"\n"}
              {row.original.digCity} {"\n"}
              {row.original.company_name} {"\n"}
              {row.original.contractor_name} {"\n"}
              {row.original.region} {"\n"}
            </div>
          );
        },
      },
      { accessor: "entered", Header: "Entered" },

      //  Assigned/Reassigned columns will be inserted here if permission exists

      {
        accessor: "assign",
        Header: "Assigned Date",
      },

      { accessor: "dueDate", Header: "Due" },
      { accessor: "completedDate", Header: "Completed" },
      {
        accessor: "code",
        Header: "Station Code",
        Cell: ({ row }) => (
          <p>{row.original.code?.map((item) => item.name).join(", ")}</p>
        ),
      },
      { accessor: "segmet", Header: "Segment" },
      {
        accessor: "isAssignPerTicket",
        Header: "Assign Per Ticket",
        Cell: ({ row }) => (
          <p>
            {row.original.isAssignPerTicket ? (
              <BiLike color="green" size={30} />
            ) : (
              <BiLike color="red" size={30} />
            )}
          </p>
        ),
      },
    ];

    //  ADDED: Conditional insertion of Assigned/Reassigned columns
    // Working:
    // - If permission is true -> add these 2 columns after "Entered"
    // - If permission is false -> do nothing, so table never sees these columns
    if (canViewAssignedDetails) {
      // index 3 = after ticketNo, location, entered
      baseColumns.splice(
        3,
        0,
        {
          accessor: "assigntoo",
          Header: "Assigned To",
          Cell: ({ row }) => <p>{row.original.assigntoo?.firstName || "-"}</p>,
        },
        {
          accessor: "reassinged",
          Header: "Reassigned To",
          Cell: ({ row }) => (
            <p>{row.original.reassigned?.firstName || "-"}</p>
          ),
        }
      );
    }

    return baseColumns;
  }, [canViewAssignedDetails]);

  //  UPDATED: Excel export should follow same permission
  // Working:
  // - If user can't view assigned details, we do NOT add those columns in export headers or rows
  const formatDateForExcel = () => {
    //  Header row
    const headerRow = [
      { v: "Ticket #", t: "s" },
      { v: "Address", t: "s" },
      { v: "Contractor", t: "s" },
      { v: "Type of Ticket", t: "s" },
      { v: "Entered Date", t: "s" },
      { v: "Assigned Date", t: "s" },
    ];

    //  Conditional headers
    if (canViewAssignedDetails) {
      headerRow.push({ v: "Assigned To", t: "s" });
      headerRow.push({ v: "Reassigned To", t: "s" });
    }

    // Remaining headers
    headerRow.push(
      { v: "Due Date", t: "s" },
      {v: "Work Begin Date", t: "s"},
      { v: "Completed Date", t: "s" },
      { v: "Station Code", t: "s" },
      { v: "Segment", t: "s" },
      { v: "Office Clear", t: "s" },
      { v: "Locate City", t: "s" },
      { v: "Caller Name", t: "s" },
      { v: "Type of Work", t: "s" },
      { v: "Per-Ticket", t: "s" }
    );

    const styledHeaderRow = headerRow.map((c) => ({
      ...c,
      s: { font: { bold: true, sz: 16, color: { rgb: "0068af" } } },
    }));

    const formattedData = [[...styledHeaderRow]];

    //  Data rows
    searchedData.forEach((item) => {
      const row = [
        { v: item.ticketNo },
        { v: `${item.digAddress}\n${item.region}\n${item.priority}` },
        { v: `${item.company_name} ${item.contractor_name} ${item.region}` },
        { v: item.priority },
        { v: item.entered ? formatDate(item.entered) : "" },
        { v: item.assign || "" },
      ];

      //  Conditional values
      if (canViewAssignedDetails) {
        row.push({
          v: item.assigntoo
            ? `${item.assigntoo.firstName} ${item.assigntoo.lastName}`
            : "",
        });

        row.push({
          v: item.reassigned
            ? `${item.reassigned.firstName} ${item.reassigned.lastName}`
            : "",
        });
      }

      row.push(
        { v: item.dueDate ? formatDate(item.dueDate) : "" },
        { v: item.workToBeginDate ? formatDate(item.workToBeginDate) : "" },
        { v: item.completedDate ? formatDate(item.completedDate) : "" },
        { v: item.code?.map((i) => i.name).join(", ") || "" },
        { v: item.segmet || "" },
        { v: item.isOfficeClear ? "Yes" : "No" },
        { v: item.region || "" },
        { v: item.contractor_name || "" },
        { v: item.ticketType || "" },
        { v: item.isAssignPerTicket ? "Yes" : "No" }
      );

      formattedData.push(row);
    });

    return formattedData;
  };

  const exportToExcel = () => {
    if (!(searchedData.length > 0)) {
      toast.error("No data found yet!");
      return;
    }
    const data = formatDateForExcel();
    const fileName = "Searched_Tickets";
    exportFileToExcel(data, fileName);
  };

  return (
    <>
      {isLoading && <Loader isLoading={isLoading} />}

      <section>
        <Heading text={"Query/Search Ticket"} btn />

        <Card>
          <SearchForm
            setSearchedData={setSearchedData}
            setIsLoading={setIsLoading}
            loadDataOnMount={false}
          />

          <Row
            className="d-flex justify-content-end mb-3"
            style={{ marginTop: "-62px" }}
          >
            <Col md={3} className={"d-flex justify-content-end mb-3"}>
              <Button onClick={exportToExcel}>
                <BiExport />
                Export
              </Button>
            </Col>
          </Row>

          {/*  Table will auto-hide columns because columns array is permission-based */}
          <SampleDataTable
            columns={columns}
            data={searchedData}
            onSelectionChange={handleSelectionChange}
          />

          <Footer />
        </Card>
      </section>
    </>
  );
};

export default SearchTicket;